import { Platform, NativeModules } from 'react-native';
import { MediaPermission } from './LocalMediaInterfaces';

const ANDROID = Object.freeze({
  CAMERA: 'android.permission.CAMERA',
  BLUETOOTH: 'android.permission.BLUETOOTH_CONNECT',
  RECORD_AUDIO: 'android.permission.RECORD_AUDIO',
  LOCATION: 'android.permission.ACCESS_FINE_LOCATION',
} as const);

type AndroidPermissionMap = typeof ANDROID;

const IOS = Object.freeze({
  APP_TRACKING_TRANSPARENCY: 'ios.permission.APP_TRACKING_TRANSPARENCY',
  BLUETOOTH_PERIPHERAL: 'ios.permission.BLUETOOTH_PERIPHERAL',
  CAMERA: 'ios.permission.CAMERA',
  MICROPHONE: 'ios.permission.MICROPHONE',
} as const);

type IOSPermissionMap = typeof IOS;

export const PERMISSIONS = Object.freeze({
  ANDROID: ANDROID as AndroidPermissionMap,
  IOS: IOS as IOSPermissionMap,
} as const);

export const RESULTS = Object.freeze({
  UNAVAILABLE: 'unavailable',
  BLOCKED: 'blocked',
  DENIED: 'denied',
  GRANTED: 'granted',
  LIMITED: 'limited',
  PROMPTED: 'prompted',
} as const);

const { RTKRNPermissions } = NativeModules;

async function request(permission) {
  return RTKRNPermissions.available.includes(permission)
    ? await RTKRNPermissions.request(permission)
    : RESULTS.UNAVAILABLE;
}

async function check(permission) {
  return RTKRNPermissions.available.includes(permission)
    ? await RTKRNPermissions.check(permission)
    : RESULTS.UNAVAILABLE;
}

export function uniq(array) {
  return array.filter(
    (item, index) => item != null && array.indexOf(item) === index
  );
}

async function requestMultiplePermissions(permissions) {
  if (Platform.OS === 'ios') {
    const output = {};
    const dedup = uniq(permissions);

    for (let index = 0; index < dedup.length; index += 1) {
      const permission = dedup[index];
      output[permission] = await request(permission);
    }
    return output;
  }
  const output = await RTKRNPermissions.requestMultiplePermissions(
    permissions,
    true
  );
  return output;
}

async function checkMultiplePermissions(permissions) {
  if (Platform.OS === 'ios') {
    const output = {};
    const dedup = uniq(permissions);

    for (let index = 0; index < dedup.length; index += 1) {
      const permission = dedup[index];
      output[permission] = await check(permission);
    }
    return output;
  }
  const output = await RTKRNPermissions.checkMultiplePermissions(permissions);
  return output;
}

export type DynamicPermissions = Array<'camera' | 'microphone'>;

const getPlatformEquiPermission = (permission, reverse = false) => {
  const android = {
    camera: PERMISSIONS.ANDROID.CAMERA,
    microphone: PERMISSIONS.ANDROID.RECORD_AUDIO,
    bluetooth: PERMISSIONS.ANDROID.BLUETOOTH,
    location: PERMISSIONS.ANDROID.LOCATION,
  };
  const ios = {
    camera: PERMISSIONS.IOS.CAMERA,
    microphone: PERMISSIONS.IOS.MICROPHONE,
    bluetooth: PERMISSIONS.IOS.BLUETOOTH_PERIPHERAL,
  };
  if (!reverse) {
    if (Platform.OS === 'android') {
      return android[permission];
    }
    return ios[permission];
  }
  if (Platform.OS === 'android') {
    const androidReverse = {};
    Object.keys(android).forEach((key) => {
      androidReverse[android[key]] = key;
    });
    return androidReverse[permission];
  }
  const iosReverse = {};
  Object.keys(ios).forEach((key) => {
    iosReverse[ios[key]] = key;
  });
  return iosReverse[permission];
};

let timeout: any;

const processPermissionResult =
  (systemPermissionRequired, audio, video) => async (status) => {
    const permissionState: { [key: string]: MediaPermission } = {
      camera: 'NOT_REQUESTED',
      microphone: 'NOT_REQUESTED',
      bluetooth: 'NOT_REQUESTED',
      location: 'NOT_REQUESTED',
    };
    systemPermissionRequired.forEach((permission) => {
      const normalPermission = getPlatformEquiPermission(permission, true);
      if (status[permission] === RESULTS.GRANTED) {
        permissionState[normalPermission] = 'ACCEPTED';
      } else if (status[permission] !== RESULTS.PROMPTED) {
        permissionState[normalPermission] = 'DENIED';
      }
    });
    const isBluetoothGranted =
      Platform.OS === 'ios' || permissionState.bluetooth !== 'NOT_REQUESTED';
    if (
      (permissionState.camera !== 'NOT_REQUESTED' || !video) &&
      (permissionState.microphone !== 'NOT_REQUESTED' || !audio) &&
      isBluetoothGranted &&
      timeout
    )
      clearInterval(timeout);
    else {
      await requestMultiplePermissions(systemPermissionRequired);
      const checkResponse = await checkMultiplePermissions(
        systemPermissionRequired
      );
      await processPermissionResult(
        systemPermissionRequired,
        audio,
        video
      )(checkResponse);
    }
    return permissionState;
  };

export const setupPermissions = async (
  audio: boolean,
  video: boolean
): Promise<{
  [key: string]: MediaPermission;
}> => {
  return new Promise<{
    [key: string]: MediaPermission;
  }>(async (resolve, reject) => {
    let statusCheck: {
      [key: string]: MediaPermission;
    };
    const systemPermissionRequired = [];
    const isIOS = Platform.OS === 'ios';
    const permissionsRequired = []
      .concat(audio ? ['microphone'] : [])
      .concat(isIOS ? [] : ['bluetooth'])
      .concat(video ? ['camera'] : []);
    permissionsRequired.forEach((permission) => {
      const systemPermissionEquivalent = getPlatformEquiPermission(permission);
      if (systemPermissionEquivalent) {
        systemPermissionRequired.push(systemPermissionEquivalent);
      }
    });

    const checkPermLoop = async (id) => {
      timeout = id;
      statusCheck = await processPermissionResult(
        systemPermissionRequired,
        audio,
        video
      )(requestResponse);
      if (statusCheck !== undefined) resolve(statusCheck);
    };
    // Requesting permission status
    const requestResponse = await requestMultiplePermissions(
      systemPermissionRequired
    );
    const timeout_sample = setInterval(
      () => checkPermLoop(timeout_sample),
      1000
    );

    if (timeout)
      statusCheck = await processPermissionResult(
        systemPermissionRequired,
        audio,
        video
      )(requestResponse);

    if (timeout) {
      clearInterval(timeout);
    }

    if (statusCheck) resolve(statusCheck);

    setTimeout(() => {
      reject(statusCheck);
    }, 20000);
  });
};

export type ResultMap = typeof RESULTS;
